import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, labelCls, Modal, Pagination, SkeletonRows, useToast } from '../ui/Kit';
import AddCandidateDialog from './AddCandidateDialog';
import CandidateRow, { Initials } from './CandidateRow';
import { DuplicateBadges, FlagBadge, fmtDate, fmtDateTime, place, SOURCE_LABEL } from './common';

// Candidate database → All candidates (bundles 4a-2, 4d): search name / e-mail / skills, filters, duplicate +
// authenticity badges, bulk actions on the selection — Add to job, Add tag, Talent pool on / off, Export (CSV). Each
// person is a compact row (initials + authenticity dot, name, title · company · city · years, the jobs they're on);
// hover / focus / tap opens one row at a time with the details and actions.
const RISK_LABEL = { low: 'Low risk', review: 'Review', high: 'High risk' };

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function JobPicker({ value, onChange }) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => { api('/api/candidates/open-jobs').then((d) => setJobs(d.jobs)).catch(() => setJobs([])); }, []);
  if (jobs === null) return <p className="text-sm text-[#64748B]">Loading jobs…</p>;
  if (!jobs.length) return <p className="text-sm text-[#64748B]">You have no open jobs.</p>;
  return (
    <select className={input} value={value || ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} aria-label="Job">
      <option value="">Choose a job…</option>
      {jobs.map((j) => <option key={j.id} value={j.id}>{j.req_id ? `${j.req_id} — ` : ''}{j.job_title}</option>)}
    </select>
  );
}

function BulkDialog({ kind, ids, onClose, onDone }) {
  const toast = useToast();
  const [job, setJob] = useState(null);
  const [tag, setTag] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setError('');
    try {
      const body = { ids, action: kind, requirement_id: job, tag, on: kind === 'talent_pool_on' ? true : undefined };
      if (kind.startsWith('talent_pool')) { body.action = 'talent_pool'; body.on = kind === 'talent_pool_on'; }
      const d = await api('/api/candidates/bulk', { method: 'POST', body });
      toast.show(`${d.done} updated${d.skipped ? ` · ${d.skipped} already done` : ''}`);
      onDone();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const title = { add_to_job: 'Add to job', add_tag: 'Add tag', talent_pool_on: 'Add to talent pool', talent_pool_off: 'Remove from talent pool' }[kind];
  return (
    <Modal open title={`${title} — ${ids.length} candidate${ids.length > 1 ? 's' : ''}`} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || (kind === 'add_to_job' && !job) || (kind === 'add_tag' && !tag.trim())} onClick={go} data-testid="bulk-confirm">{busy ? 'Working…' : title}</button></>}>
      {kind === 'add_to_job' && <><p className="text-sm text-[#64748B] mb-2">They join the job at “Sourced”. People already on it are skipped.</p><JobPicker value={job} onChange={setJob} /></>}
      {kind === 'add_tag' && <label className="block"><span className={labelCls}>Tag</span><input className={input} value={tag} onChange={(e) => setTag(e.target.value)} maxLength={40} autoFocus /></label>}
      {kind.startsWith('talent_pool') && <p className="text-sm text-[#334155]">{kind === 'talent_pool_on' ? 'They will show in talent-pool searches for future roles.' : 'They will no longer show in talent-pool searches.'}</p>}
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export default function CandidateList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('');
  const [pool, setPool] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState(null);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [addOne, setAddOne] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams({ q: query, page: String(page), page_size: '25' });
    if (source) p.set('source', source);
    if (pool) p.set('talent_pool', pool);
    if (flagged) p.set('flagged', 'true');
    setError(null);
    return api(`/api/candidates?${p}`).then(setData).catch((e) => setError(e));
  }, [query, page, source, pool, flagged]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { setQuery(q.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const rows = data ? data.rows : [];
  const allOnPage = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const exportCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/candidates/bulk`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ ids: selected, action: 'export' }) });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'candidates.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.show(`${selected.length} exported`);
    } catch (e) { toast.show(e.message, 'error'); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
        <label className="block flex-1"><span className="sr-only">Search candidates</span>
          <input className={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, e-mail, title or skills…" data-testid="candidate-search" /></label>
        <div className="grid grid-cols-2 sm:flex gap-2">
          <select className={input + ' sm:w-44'} value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} aria-label="Source">
            <option value="">All sources</option>{Object.entries(SOURCE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={input + ' sm:w-40'} value={pool} onChange={(e) => { setPool(e.target.value); setPage(1); }} aria-label="Talent pool">
            <option value="">Everyone</option><option value="true">Talent pool</option><option value="false">Not in the pool</option></select>
          <label className="inline-flex items-center gap-2 text-sm px-2"><input type="checkbox" checked={flagged} onChange={(e) => { setFlagged(e.target.checked); setPage(1); }} /> Flagged</label>
          <button className={btnPrimary} onClick={() => setAdding(true)} data-testid="add-candidate">Add candidate</button>
        </div>
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-[#E8F1FB] border border-[#C7DDF5] rounded-xl px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-[#003D82] mr-2">{selected.length} selected</span>
          <button className={btnSecondary} onClick={() => setBulk('add_to_job')}>Add to job</button>
          <button className={btnSecondary} onClick={() => setBulk('add_tag')}>Add tag</button>
          <button className={btnSecondary} onClick={() => setBulk('talent_pool_on')}>Talent pool on</button>
          <button className={btnSecondary} onClick={() => setBulk('talent_pool_off')}>Talent pool off</button>
          <button className={btnSecondary} onClick={exportCsv} data-testid="bulk-export">Export</button>
          <button className="text-sm text-[#475569] ml-auto hover:underline" onClick={() => setSelected([])}>Clear</button>
        </div>
      )}
      {error ? (
        <div className={card + ' p-5'}><InlineError>{error.message}{error.reference ? ` (reference ${error.reference})` : ''}</InlineError>
          <button className={btnSecondary + ' mt-3'} onClick={load}>Try again</button></div>
      ) : !data ? <div className={card}><SkeletonRows rows={6} cols={5} /></div> : !rows.length ? (
        <div className={card + ' p-2'}><EmptyState title={query || source || pool || flagged ? 'No candidates match' : 'No candidates yet'}
          hint={query || source || pool || flagged ? 'Try fewer filters.' : 'Import resumes or add a candidate to get started.'}
          action={<button className={btnPrimary} onClick={() => setAdding(true)}>Add candidate</button>} /></div>
      ) : (
        <div className={card + ' overflow-hidden'}>
          <div className="flex items-center gap-3 px-4 py-2 bg-[#F8FAFC] text-[11px] uppercase tracking-wide text-[#64748B] border-b border-[#EEF1F6]">
            <input type="checkbox" aria-label="Select all on this page" checked={allOnPage}
              onChange={() => setSelected((s) => (allOnPage ? s.filter((id) => !rows.some((r) => r.id === id)) : [...new Set([...s, ...rows.map((r) => r.id)])]))} />
            <span className="flex-1">Candidate</span><span className="hidden sm:block">Jobs</span>
          </div>
          <ul data-testid="candidate-table">
            {rows.map((r) => (
              <CandidateRow key={r.id} id={r.id} open={openId === r.id} onOpen={setOpenId} onClose={(id) => setOpenId((o) => (o === id ? null : o))}
                name={r.full_name} link={`/candidates/${r.id}`} jobs={r.jobs || []} hidden={r.hidden_jobs || 0}
                subtitle={[r.current_title, r.current_company, r.city, r.total_years != null ? `${r.total_years} yrs` : null].filter(Boolean).join(' · ') || '—'}
                lead={<><input type="checkbox" aria-label={`Select ${r.full_name}`} checked={selected.includes(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} />
                  <Initials name={r.full_name} risk={r.risk} /></>}>
                <div className="flex flex-wrap gap-1 mb-2"><FlagBadge count={r.flags} /><DuplicateBadges tiers={r.duplicate_badges} />
                  {r.talent_pool && <span className="px-2 py-[3px] rounded-md text-[11px] font-semibold bg-[#E6F6F5] text-[#00877D]">Talent pool</span>}
                  {(r.tags || []).slice(0, 3).map((t) => <span key={t} className="px-2 py-[3px] rounded-md text-[11px] font-semibold bg-[#F1F5F9] text-[#334155]">{t}</span>)}</div>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[12.5px]">
                  <div><dt className="text-[#64748B]">Authenticity</dt><dd className="font-semibold">{RISK_LABEL[r.risk] || 'Not checked yet'}</dd></div>
                  <div><dt className="text-[#64748B]">Expected</dt><dd className="font-semibold">{(r.facts || {}).expected || '—'}</dd></div>
                  <div><dt className="text-[#64748B]">Notice</dt><dd className="font-semibold">{(r.facts || {}).notice || '—'}</dd></div>
                  <div><dt className="text-[#64748B]">Work auth</dt><dd className="font-semibold">{(r.facts || {}).work_authorization || '—'}</dd></div>
                  <div><dt className="text-[#64748B]">Source</dt><dd>{SOURCE_LABEL[r.source] || r.source}</dd></div>
                  <div><dt className="text-[#64748B]">Location</dt><dd>{place(r) || '—'}</dd></div>
                  <div><dt className="text-[#64748B]">Last activity</dt><dd>{fmtDateTime(r.last_activity_at)}</dd></div>
                  <div><dt className="text-[#64748B]">Added</dt><dd>{fmtDate(r.created_at)}</dd></div>
                </dl>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <button type="button" className={btnPrimary + ' text-xs py-1.5'} onClick={() => setAddOne(r.id)} data-testid="row-add-to-job">Add to job</button>
                  <button type="button" className={btnSecondary + ' text-xs py-1.5'} onClick={() => navigate(`/candidates/${r.id}`)}>Open profile</button>
                </div>
              </CandidateRow>
            ))}
          </ul>
          <div className="px-4 pb-3"><Pagination page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} noun="candidates" /></div>
        </div>
      )}
      {bulk && <BulkDialog kind={bulk} ids={selected} onClose={() => setBulk(null)} onDone={() => { setBulk(null); load(); }} />}
      {addOne && <BulkDialog kind="add_to_job" ids={[addOne]} onClose={() => setAddOne(null)} onDone={() => { setAddOne(null); load(); }} />}
      <AddCandidateDialog open={adding} onClose={() => setAdding(false)} onAdded={load} />
    </div>
  );
}
